import { memo, useCallback, useMemo } from 'react';
import { isString } from 'radash';
import {
  canUseNewTimelineForUnmigratedLiveEvent,
  doesClipWorkOnAlignedStreams,
  getAllCompositeSegments,
  getClipBroadcastId,
  isClipAudiogram,
  isClipLayoutDefault,
  isNewLayoutsMigrated,
  isOriginalLayoutAvailable,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { isLayoutAudiogram } from '@goldcast/player-config/src/utils/layout';
import { LayoutType } from '@goldcast/player-config/src/types/asset-types';
import { Scene } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import MatchSpeakersCTA from '../MatchSpeakersCTA';
import useClipCustomiserLayoutPopup from '../useClipLayoutPopupOptions';
import ClipToggleOptions from './ClipToggleOptions';
import { useIsNameCardAutoHidden } from '../useIsNameCardAutoHidden';
import { useGlobalVideoToggle } from './useGlobalVideoToggle';
import ClipCustomizerMultipleSceneWarning from '../ClipCustomizerMultipleSceneWarning';
import ClipCustomizerMigrateNudge from '../ClipCustomizerMigrateNudge';
import { CLIP_CUSTOMIZER_WARNING } from '../../../ClipCustomizer/useClipCustomizerWarnings';
import ClipCustomizerPanel from '../../../ClipCustomizer/ClipCustomizerPanel/ClipCustomizerPanel';
import ClipCustomizerLayoutCard from './ClipCustomizerLayoutCard';
import DisabledOptionTooltips from './DisabledOptionTooltips';
import LayoutSegmentedControl from './LayoutSegmentedControl';
import AspectRatioSelector from './AspectRatioSelector';
import ClipCustomizerNewLayoutsMigrateNudge from './ClipCustomizerNewLayoutsMigrateNudge';
import PipCornerSelector from './PipCornerSelector';
import {
  NEW_LAYOUT_CARD_ORDER,
  PIP_CORNER_TO_POSITION,
  PipCorner,
  pipPositionToCorner,
  SPEAKER_POSITION_OPTIONS,
  SPEAKER_TILE_SHAPE_OPTIONS,
  SpeakerPosition,
  SpeakerTileShape
} from './constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import {
  toggleHideBorders,
  toggleRoundCorners,
  toggleSpeakerLabels,
  isLayoutsWithVisionDataEnabled,
  updateSpeakerDirection,
  updatePipShape,
  updatePipPosition
} from '@/stores/clip';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

function getScenesForClip(clip: Clip): Scene[] {
  const compositeSegments = getAllCompositeSegments(clip);
  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  if (isSplitsEnabled) {
    return structuredClone(compositeSegments.flatMap(segment => segment.segment_metadata?.scenes ?? []));
  }

  return structuredClone(clip.asset_metadata.scenes) || [];
}

function ClipCustomizerNewLayoutPopup({
  warnings,
  onClose
}: Pick<CLIP_CUSTOMIZER_WARNING, 'warnings'> & { onClose: () => void }) {
  const { clipId, clipData } = useClipsContext();
  const broadcastId = getClipBroadcastId(clipData) ?? '';
  const scenes = useMemo(() => getScenesForClip(clipData), [clipData]);

  const { tracksToRender, handleTrackToggle } = useGlobalVideoToggle({
    broadcastId,
    clipId,
    scenes
  });

  const isNewLayoutsEnabled = isNewLayoutsMigrated(clipData);
  const speakerPosition: SpeakerPosition = clipData.asset_metadata.speaker_direction ?? 'RIGHT';
  const onSpeakerPositionChange = useCallback(
    (value: SpeakerPosition) => {
      updateSpeakerDirection(clipId, value);
    },
    [clipId]
  );

  const speakerTileShape: SpeakerTileShape = clipData.asset_metadata.pip_shape === 'RECTANGULAR' ? 'SQUARE' : 'ROUND';
  const onSpeakerTileShapeChange = useCallback(
    (value: SpeakerTileShape) => {
      updatePipShape(clipId, value === 'ROUND' ? 'CIRCULAR' : 'RECTANGULAR');
    },
    [clipId]
  );

  const pipCorner = pipPositionToCorner(clipData.asset_metadata.pip_position);
  const onPipCornerChange = useCallback(
    (value: PipCorner) => {
      updatePipPosition(clipId, PIP_CORNER_TO_POSITION[value]);
    },
    [clipId]
  );

  const onNameCardToggle = useCallback(
    (value: boolean) => {
      toggleSpeakerLabels(clipId, value);
    },
    [clipId]
  );

  const onBorderToggle = useCallback(
    (value: boolean) => {
      toggleHideBorders(clipId, !value);
    },
    [clipId]
  );

  const isRoundCornersOn = !!clipData.asset_metadata.round_corners;
  const onRoundCornersToggle = useCallback(
    (value: boolean) => {
      toggleRoundCorners(clipId, value);
    },
    [clipId]
  );

  const isOriginalAvailable = useMemo(() => {
    return isOriginalLayoutAvailable(clipData);
  }, [clipData]);

  const isSpeakerLayoutAvailable = useMemo(() => {
    if (
      doesClipWorkOnAlignedStreams(clipData) ||
      isLayoutsWithVisionDataEnabled(clipData) ||
      canUseNewTimelineForUnmigratedLiveEvent(clipData)
    ) {
      return true;
    }
    return false;
  }, [clipData]);

  const { isBorderDisabled, showSpeakerPosition, isAudiogram } = useMemo(() => {
    const clipLayout = clipData.asset_metadata.layout;
    const _isLayoutAudiogram = isLayoutAudiogram(clipLayout);

    return {
      isBorderDisabled: _isLayoutAudiogram,
      showSpeakerPosition: clipLayout === 'FOCUS',
      isAudiogram: _isLayoutAudiogram
    };
  }, [clipData.asset_metadata.layout]);

  const { showNameCard, isSpeakerLabelOn } = useMemo(() => {
    const sceneCount = clipData.asset_metadata.scenes?.length ?? 0;
    const hideNameCard = !(sceneCount > 1) && isClipLayoutDefault(clipData);
    return {
      showNameCard: !hideNameCard, // if there is only one scene then clip layout and scene layout are in sync
      isSpeakerLabelOn: !!clipData.asset_metadata.magicLayout?.showSpeakerLabels || isClipAudiogram(clipData)
    };
  }, [clipData]);

  const triggerSpeakerIdentification = useCallback((params = {}) => {
    EventBus.dispatch(CustomEvents.OpenSpeakersIdentification, params);
  }, []);

  const {
    showMatchSpeakersCTA,
    originalLayoutDisableReason,
    gridDisableReason,
    focusDisableReason,
    pipDisableReason,
    fullScreenDisableReason,
    fitFillDisabledReason
  } = useClipCustomiserLayoutPopup();

  const disabledSpeakerLabelToggle = useMemo(() => {
    return isClipAudiogram(clipData);
  }, [clipData]);

  const isNameCardAutoHidden = useIsNameCardAutoHidden();

  const disableReasonByLayout = useMemo<Partial<Record<LayoutType, string | null>>>(
    () => ({
      DEFAULT: originalLayoutDisableReason,
      SPEAKER: fullScreenDisableReason,
      GRID: gridDisableReason,
      FOCUS: focusDisableReason,
      PIP: pipDisableReason,
      AUDIOGRAM: null
    }),
    [originalLayoutDisableReason, fullScreenDisableReason, gridDisableReason, focusDisableReason, pipDisableReason]
  );

  const visibleCards = useMemo(() => {
    return NEW_LAYOUT_CARD_ORDER.filter(layout => {
      if (layout === 'DEFAULT') {
        return isOriginalAvailable;
      }
      if (layout === 'AUDIOGRAM') {
        return true;
      }
      return isSpeakerLayoutAvailable;
    });
  }, [isOriginalAvailable, isSpeakerLayoutAvailable]);

  const showSpeakerPositionOption = isNewLayoutsEnabled && showSpeakerPosition;
  const showPipOptions = isNewLayoutsEnabled && clipData.asset_metadata.layout === 'PIP';

  return (
    <ClipCustomizerPanel heading="Layouts" onClose={onClose} closeTrackingId="layouts-popup-close">
      <div className="flex flex-col gap-4">
        <AspectRatioSelector />
        <ClipCustomizerNewLayoutsMigrateNudge />
        <ClipCustomizerMigrateNudge />
        <ClipCustomizerMultipleSceneWarning skipMargin />
        <div className="flex w-full flex-col gap-3">
          <div className="text-xs">Layouts</div>
          <div className="rounded-xl border border-slate-100 p-4">
            <div className="grid grid-cols-2 gap-x-2.5 gap-y-3">
              {visibleCards.map(layout => (
                <ClipCustomizerLayoutCard
                  key={layout}
                  magicLayoutOption={layout}
                  disabled={isString(disableReasonByLayout[layout])}
                  disabledReason={disableReasonByLayout[layout] ?? ''}
                />
              ))}
            </div>
            <DisabledOptionTooltips
              place="top"
              options={visibleCards.map(layout => ({
                id: `magic-layout-card-${layout}`,
                disabledReason: disableReasonByLayout[layout]
              }))}
            />
            {(showSpeakerPositionOption || showPipOptions) && (
              <div className="mt-6">
                {showSpeakerPositionOption && (
                  <LayoutSegmentedControl
                    label="Speaker position"
                    options={SPEAKER_POSITION_OPTIONS}
                    value={speakerPosition}
                    onChange={onSpeakerPositionChange}
                  />
                )}
                {showPipOptions && (
                  <LayoutSegmentedControl
                    label="Speaker tile shape"
                    options={SPEAKER_TILE_SHAPE_OPTIONS}
                    value={speakerTileShape}
                    onChange={onSpeakerTileShapeChange}
                  />
                )}
                {showPipOptions && <PipCornerSelector value={pipCorner} onChange={onPipCornerChange} />}
              </div>
            )}
          </div>
        </div>
        <div className="flex w-full flex-col gap-3">
          <div className="text-xs">Speakers</div>
          {showMatchSpeakersCTA && (
            <MatchSpeakersCTA
              onClick={() =>
                triggerSpeakerIdentification({
                  isInlineIdentification: true,
                  layout: clipData.asset_metadata.layout
                })
              }
              warnings={warnings}
              skipMargin
            />
          )}
          <div className="rounded-xl border border-slate-100 p-4 pb-1">
            <ClipToggleOptions
              showNameCard={showNameCard}
              isSpeakerLabelOn={isSpeakerLabelOn}
              onNameCardToggle={onNameCardToggle}
              showBorder={!clipData.asset_metadata.hide_borders}
              onBorderToggle={onBorderToggle}
              isBorderDisabled={isBorderDisabled}
              isRoundCornersOn={isRoundCornersOn}
              onRoundCornersToggle={onRoundCornersToggle}
              fitFillDisabledReason={fitFillDisabledReason}
              disabledSpeakerLabelToggle={disabledSpeakerLabelToggle}
              tracks={tracksToRender}
              onToggleTrack={handleTrackToggle}
              isNameCardAutoHidden={isNameCardAutoHidden}
              hideParticipants={isAudiogram || !isSpeakerLayoutAvailable}
              hideRoundCorners={!isSpeakerLayoutAvailable}
            />
          </div>
        </div>
      </div>
    </ClipCustomizerPanel>
  );
}

export default memo(ClipCustomizerNewLayoutPopup);
